import React, { useState, useEffect } from 'react';
import { Button, Col, Divider, Drawer, DatePicker, Form, Input, InputNumber, Row, Select, Space, Switch, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { generarDetails } from '../../../../../apis/PeriodosApi';

const GenerarPeriodos = ({ periodId, period, open, finish }) => {
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generando, setGenerando] = useState(false);

  const onClose = () => {
    setPreview(null);
    finish(false);
  };

  useEffect(() => {
    if (open) {
      setPreview(null);
      form.resetFields();
      form.setFieldsValue({
        anio: dayjs().year(),
        num_periodo_inicial: 1,
        dias_periodo: 7,
        horas_periodo: 48,
        tipo_nomina: 'O',
        clave_sat: period?.clave_sat || '',
        periodo_especial: false,
        no_descontar_saldos: false,
        solo_excepciones: false,
        incluir_bajas: false,
      });
    }
  }, [open]);

  const buildPayload = (dryRun) => {
    const values = form.getFieldsValue();
    return {
      periodo_id: periodId,
      anio: values.anio,
      fecha_inicio: values.fecha_inicio.format('YYYY-MM-DD'),
      fecha_final_referencia: values.fecha_final_referencia.format('YYYY-MM-DD'),
      num_periodo_inicial: values.num_periodo_inicial,
      dias_periodo: values.dias_periodo,
      horas_periodo: values.horas_periodo,
      tipo_nomina: values.tipo_nomina || 'O',
      clave_sat: values.clave_sat || null,
      periodo_especial: Boolean(values.periodo_especial),
      no_descontar_saldos: Boolean(values.no_descontar_saldos),
      solo_excepciones: Boolean(values.solo_excepciones),
      incluir_bajas: Boolean(values.incluir_bajas),
      dry_run: dryRun,
    };
  };

  const handlePrevisualizar = async () => {
    try {
      await form.validateFields();
    } catch (e) {
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await generarDetails(buildPayload(true));
      if (response.ok) {
        setPreview(response.payload);
        message.success(`Se calcularon ${response.payload.total} periodo(s). Revisa la tabla y confirma para generar.`);
      } else {
        message.error(response?.message || 'No se pudieron previsualizar los periodos');
      }
    } catch (e) {
      message.error('Error al previsualizar los periodos');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerar = async () => {
    if (!preview) return;

    setGenerando(true);
    try {
      const response = await generarDetails(buildPayload(false));
      if (response.ok) {
        const result = response.payload;
        let mensaje = `Se generaron ${result.total} periodo(s) correctamente.`;
        if (result.ultimo_rebasa_referencia) {
          mensaje += ` El último periodo termina el ${dayjs(result.fecha_final_real).format('DD/MM/YYYY')}, después de la fecha final de referencia.`;
        }
        message.success(mensaje);
        setPreview(null);
        finish(true);
      } else {
        message.error(response?.message || 'No se pudieron generar los periodos');
      }
    } catch (e) {
      message.error('Error al generar los periodos');
    } finally {
      setGenerando(false);
    }
  };

  const columns = [
    { title: 'Núm.', dataIndex: 'num_periodo', key: 'num_periodo', width: 70 },
    { title: 'Año', dataIndex: 'anio', key: 'anio', width: 70 },
    { title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion', width: 120 },
    { title: 'Fecha Inicial', dataIndex: 'fecha_inicial', key: 'fecha_inicial', render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-') },
    { title: 'Fecha Final', dataIndex: 'fecha_final', key: 'fecha_final', render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-') },
    { title: 'Mes', dataIndex: 'mes', key: 'mes', width: 60 },
    { title: 'Num Per Mes', key: 'num_per_mes', width: 110, render: (_, record) => `${record.num_per_del_mes} / ${record.total_per_del_mes}` },
    { title: 'Fin de mes', dataIndex: 'fin_de_mes', key: 'fin_de_mes', width: 100, render: (value) => (value ? <Tag color="green">Sí</Tag> : <Tag>No</Tag>) },
  ];

  return (
    <Drawer
      title="Generar periodos"
      width={780}
      placement="right"
      onClose={onClose}
      open={open}
      closable={true}
      height="80%"
      styles={{ body: { paddingBottom: 80 } }}
    >
      <Form layout="vertical" form={form} hideRequiredMark>
        <Row gutter={16}>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="anio"
              label="Año"
              rules={[{ required: true, message: 'Por favor agrega el año' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1900} max={2200} placeholder="Año de los periodos" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="fecha_inicio"
              label="Fecha de inicio"
              rules={[{ required: true, message: 'Por favor agrega la fecha de inicio' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Inicio del primer periodo" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="fecha_final_referencia"
              label="Fecha final (referencia)"
              rules={[{ required: true, message: 'Por favor agrega la fecha final de referencia' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Referencia de fin" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="num_periodo_inicial"
              label="Número de periodo inicial"
              rules={[{ required: true, message: 'Por favor agrega el número de periodo inicial' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} placeholder="Desde donde se comienza" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="dias_periodo"
              label="Días periodo"
              rules={[{ required: true, message: 'Por favor agrega los días del periodo' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} placeholder="Días de cada periodo" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="horas_periodo"
              label="Horas periodo"
              rules={[{ required: true, message: 'Por favor agrega las horas del periodo' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} placeholder="Horas de cada periodo" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="tipo_nomina"
              label="Tipo de nomina"
              rules={[{ required: true, message: 'Por favor agrega el tipo de nomina' }]}
            >
              <Select placeholder="Por favor agrega el tipo de nomina">
                <Select.Option value="O">O</Select.Option>
                <Select.Option value="E">E</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="clave_sat"
              label="Clave SAT del periodo"
              rules={[{ required: true, message: 'Por favor agrega la clave SAT del periodo' }]}
            >
              <Input placeholder="Por favor agrega la clave SAT del periodo" />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item name="periodo_especial" label="Periodo especial" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item name="no_descontar_saldos" label="No descontar saldos" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item name="solo_excepciones" label="Solo excepciones" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item name="incluir_bajas" label="Incluir bajas" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Space>
        <Button onClick={handlePrevisualizar} loading={loadingPreview} type="primary" ghost>
          Previsualizar
        </Button>
        <Button onClick={handleGenerar} loading={generando} disabled={!preview} type="primary">
          Generar
        </Button>
      </Space>

      {preview && (
        <>
          <Divider orientation="left">Vista previa ({preview.total} periodo(s))</Divider>
          <Table
            size="small"
            rowKey={(record) => `${record.anio}-${record.num_periodo}`}
            dataSource={preview.periodos}
            columns={columns}
            pagination={false}
            scroll={{ y: 320 }}
          />
        </>
      )}
    </Drawer>
  );
};

export default GenerarPeriodos;
